/**
 * Pulse — the universal Health-Wall (docs/lifecycle-spine/PRD.md §0, §8).
 *
 * The L0 aggregate wall, ported from the approved `_zoom.tsx` prototype
 * (`AggregateLevel` / `AggCluster` / `StatusStrip`) and wired to real data via
 * the `pulse` contract. One cluster per function band; one bar per touchpoint;
 * the low bars are the systemic bottlenecks. LOCKED visual language (§0):
 *
 *   · mono tabular-nums for every instrument number (status strip, bar values)
 *   · NO icons on bars (icons only on group headers / chrome, via <Icon>)
 *   · a target LINE across each cluster's bar track
 *   · hover any bar → tooltip (group · label, kind, value vs target, subjects, at-risk)
 *   · three semantic band colors banded against EACH touch's own `target`
 *   · ghost bars (dashed, empty) for planned / null touchpoints — never faked
 *   · (the status strip — overall · N subjects · N touchpoints · N bottlenecks
 *     with a "jump to worst →" affordance — was REMOVED entirely, from both the
 *     L0 wall and the L1 breakdown detail; the wall reads as bars alone now)
 *   · a per-bar / per-group target chip (`target 70 · 64% attain`) from calibration
 *
 * Three sizes share one bar language (§8 embeds):
 *   · "full"  — the mode dashboard wall (status strip + clusters + group footers)
 *   · "strip" — a compact horizontal wall atop a list (shorter bars, no strip)
 *   · "mini"  — a tiny sparkline wall for one entity (an Object/Dossier header)
 *
 * P2 — the wall is a NAVIGATOR (docs/lifecycle-spine/PRD.md §8). Three altitudes
 * with animated dives + deep-links (ported from the `_zoom.tsx` prototype):
 *   · L0 BREAKDOWN target → clicking a bottleneck bar sets `?g&?t`, fetches
 *     `pulse.breakdown`, and renders the L1 wall (one bar per subject worst-first
 *     for portfolio scope — "who's dragging it down" — or the component rows for
 *     single-subject scope) inside a <ZoomFrame> (deeper grows in, climbing pulls
 *     back), with a <Breadcrumb> above. A breadcrumb crumb clears back up.
 *   · L2 → clicking an L1 subject bar navigates to its Object page (`/o/<uid>`),
 *     a real route navigation. Drill hrefs are computed SERVER-SIDE (the block is
 *     IA-agnostic, §8 drillTargetOf); the block only follows them.
 * Deep-links: `?g=<groupKey>&t=<touchId>&c=<entityId>` (ported `readPath`); the
 * controller stays on the same page so the dashboardSlot re-renders.
 *
 * The block EXTENDS primitives (<Card>, <Icon>, <Button>, <Link>) and sources
 * the BAND tokens locally (matching `_shared.tsx`). Drill nav uses next/navigation
 * — never raw `fetch`, never `next/server`, never `excellent/src/*`.
 *
 * THIS FILE is the wall renderer + drill controller. The cohesive sub-views are
 * sibling modules so no single block is oversized (#1144):
 *   · `pulse-wall-shared`    — band tokens + classification + the public types.
 *   · `pulse-wall-cells`     — AggCluster / ClusterCard / EmptyCell / Bar (the bars).
 *   · `pulse-wall-tips`      — WallTipLayer, the single cursor-following tooltip.
 *   · `pulse-wall-breakdown` — the L1 breakdown drill + breadcrumb (Crumb/Sep).
 */

import React, { useEffect, useRef, useState } from "react";
import { useNavigate as useRouter, useSearch as useSearchParams, usePathname } from "../../router";
import { Card, CardContent } from "../../card";
import { useHydrated } from "../../use-hydrated";
import { useApi } from "../../api/client";
import { pulse, type PulseGroupDTO } from "../../api/contracts/pulse";
import {
  modeOfScope,
  bandState,
  isLive,
  PULSE_STAGE,
  type PulseProps,
  type PulseSize,
  type PulseViewMode,
} from "./pulse-wall-shared";
import { AggCluster, ClusterCard, EmptyCell } from "./pulse-wall-cells";
import { WallTipLayer } from "./pulse-wall-tips";
import { PulseBreakdown, Crumb, Sep } from "./pulse-wall-breakdown";

// Re-export the public surface so importers of this module path keep working:
// `index.ts` re-exports Pulse/PulseProps/PulseSize; `granular-pulse.tsx` imports
// WallTipLayer directly. The sub-view files are internal (relative-imported here).
export { WallTipLayer } from "./pulse-wall-tips";
export type { PulseProps, PulseSize, PulseViewMode } from "./pulse-wall-shared";

/** Does this group match the current sub-page section? Drives the strip's subtle
 *  "you are here" ring (no de-emphasis of the others — that's the whole point).
 *  Match the band/lane SLUG (`g.key`) only — NOT `g.domain.area`, which is
 *  non-unique (Build/Ship/Quality/Adoption all share "development", so an area
 *  match lit all four bands at once on `/product?area=development`). Navigation
 *  keys on the slug everywhere (the wall route + section-health both build
 *  `/<mode>?area=<g.key>`), so the ring keys on it too — exactly one band, or
 *  none on a real (non-lane) domain page. */
function isCurrentSection(g: PulseGroupDTO, current?: string): boolean {
  return current != null && current !== "" && g.key === current;
}

const TRACK_FULL = 168;
const TRACK_STRIP = 72;
const TRACK_MINI = 44;

/**
 * Pulse — the navigator (P2). For the `full` mode dashboard it reads the
 * `?g/?t/?c` deep-link and drills between altitudes (L0 wall ⇄ L1 breakdown,
 * animated); `strip`/`mini` embeds render the plain non-drilling wall (an embed
 * never owns the page's nav state — it links out via its own surface instead).
 */
export function Pulse(props: PulseProps) {
  if ((props.size ?? "full") === "full") return <PulseNavigator {...props} />;
  return <PulseWall {...props} />;
}

// ── the drill controller (full size only) — reads ?g/?t/?c, dives to L1 ────────

/** Deep-link path (ported `_zoom.tsx` Path/readPath). `?c` is a deeper
 *  single-subject component drill; portfolio L2 is a real `/o/<uid>` nav. */
interface DrillPath {
  groupKey?: string;
  touchId?: string;
  entityId?: number;
}

function readDrillPath(sp: URLSearchParams): DrillPath {
  const g = sp.get("g") ?? undefined;
  if (!g) return {};
  const t = sp.get("t") ?? undefined;
  if (!t) return { groupKey: g };
  const cRaw = sp.get("c");
  const c = cRaw != null ? Number(cRaw) : NaN;
  return Number.isFinite(c) ? { groupKey: g, touchId: t, entityId: c } : { groupKey: g, touchId: t };
}

const drillDepth = (p: DrillPath) => (p.entityId != null ? 2 : p.touchId ? 1 : 0);

function PulseNavigator({ scope, rollup, weight, days, editable, projectSlug, className }: PulseProps) {
  const router = useRouter();
  const pathname = usePathname();
  const sp = useSearchParams();
  const path = readDrillPath(new URLSearchParams(sp.toString()));

  // Section drill (SECTION-PULSE-PRD §5): on a mode section rollup, a section bar
  // navigates to `?sec=<groupKey>` — THIS section's per-touchpoint cluster, shown
  // IN-WALL here by filtering the full mode wall (`scope`) to that group. The key
  // is the section identity (the `domain.area` is not unique). The touchpoints
  // live in the full mode wall, so the breakdown stays on `scope`. Only honored on
  // a section rollup (`rollup === "sections"`).
  const sec = rollup === "sections" ? sp.get("sec") ?? undefined : undefined;

  // Animate direction: deeper → grow in, climbing → pull back (ported ZoomFrame).
  // A section dive (`?sec`) is one altitude; a touch breakdown atop it is deeper.
  const depth = drillDepth(path) + (sec ? 1 : 0);
  const prevDepth = useRef(depth);
  const dir: "in" | "out" = depth >= prevDepth.current ? "in" : "out";
  useEffect(() => {
    prevDepth.current = depth;
  }, [depth]);

  // Did this session drill in via a bar click (vs. a cold deep-link to ?g&t)?
  // Drilling DOWN pushes a history entry so the browser Back button climbs back
  // out cleanly; the in-app "back to the wall" then mirrors Back via router.back()
  // — but a cold deep-link has nothing to pop, so it falls back to a push.
  const drilledInApp = useRef(false);

  // Stay on the same page (keep the dashboardSlot mounted) — change only the
  // query. `history:"push"` adds a Back-able entry (a drill); "replace" doesn't.
  const setQuery = (
    updates: Record<string, string | null | undefined>,
    history: "push" | "replace" = "push",
  ) => {
    const next = new URLSearchParams(sp.toString());
    for (const [k, v] of Object.entries(updates)) {
      if (v == null || v === "") next.delete(k);
      else next.set(k, v);
    }
    const qs = next.toString();
    const url = qs ? `${pathname}?${qs}` : pathname;
    if (history === "push") router.push(url, { scroll: false });
    else router.replace(url, { scroll: false });
  };

  // Drill DOWN (bar click) → push, so Back returns to the wall and Forward redoes
  // the dive. Both directions stay instant: same pathname, the client dashboardSlot
  // just re-reads the query (no server round-trip).
  const drillToTouch = (groupKey: string, touchId: string) => {
    drilledInApp.current = true;
    setQuery({ g: groupKey, t: touchId, c: null }, "push");
  };
  // Climb UP (breadcrumb / "back to the wall") → pop the drill we pushed, so the
  // in-app back behaves identically to the browser Back button (instant, no extra
  // forward entry). A cold deep-link (never drilled) has no entry to pop → push.
  const climbToAggregate = () => {
    if (drilledInApp.current) {
      drilledInApp.current = false;
      router.back();
    } else {
      setQuery({ g: null, t: null, c: null }, "push");
    }
  };

  // Climb the section dive (`?sec`) back to the section rollup — clear `sec` (and
  // any open breakdown). A bar-click dive pushed history, so mirror Back.
  const climbToSections = () => {
    if (drilledInApp.current) {
      drilledInApp.current = false;
      router.back();
    } else {
      setQuery({ sec: null, g: null, t: null, c: null }, "push");
    }
  };

  // L1 — a touch is open: fetch its breakdown + render the breakdown level. The
  // touchpoints live in the full mode wall, so the breakdown stays on `scope`;
  // climbing keeps `?sec`, returning to the focused section cluster.
  if (path.touchId) {
    return (
      <div className={className}>
        <ZoomFrame keyId={`t:${path.groupKey}:${path.touchId}`} dir={dir}>
          <PulseBreakdown
            scope={scope}
            touchId={path.touchId}
            weight={weight}
            days={days}
            onClimb={climbToAggregate}
          />
        </ZoomFrame>
      </div>
    );
  }

  // L0.6 — a section is open (mode section rollup `?sec`): THIS section's
  // per-touchpoint cluster, filtered from the full mode wall, with a crumb back
  // to the section rollup. The cluster's touchpoints drill exactly like the mode
  // wall (the `?g/?t` breakdown above).
  if (sec) {
    return (
      <div className={className}>
        <ZoomFrame keyId={`sec:${sec}`} dir={dir}>
          <div className="space-y-3">
            <div className="flex items-center gap-0.5">
              <Crumb onClick={climbToSections}>All sections</Crumb>
              <Sep />
              <span className="px-1.5 py-0.5 type-subtext capitalize text-foreground">{sec}</span>
            </div>
            <PulseWall
              scope={scope}
              size="full"
              focusGroup={sec}
              weight={weight}
              days={days}
              editable={editable}
              projectSlug={projectSlug}
              onTouch={drillToTouch}
            />
          </div>
        </ZoomFrame>
      </div>
    );
  }

  // L0 — the aggregate wall, clickable (a bottleneck bar drills to L1; on a
  // section rollup, a section bar navigates to its `?sec` per-touchpoint wall).
  return (
    <ZoomFrame keyId="agg" dir={dir} className={className}>
      <PulseWall
        scope={scope}
        size="full"
        rollup={rollup}
        weight={weight}
        days={days}
        editable={editable}
        projectSlug={projectSlug}
        onTouch={drillToTouch}
      />
    </ZoomFrame>
  );
}

// ── the wall (L0) — the aggregate, now optionally clickable (P1 body) ──────────

function PulseWall({
  scope,
  size = "full",
  rollup,
  weight,
  days,
  editable,
  projectSlug,
  current,
  className,
  onTouch,
  focusGroup,
}: PulseProps & {
  onTouch?: (groupKey: string, touchId: string) => void;
  /** Section dive (SECTION-PULSE-PRD §5): render ONLY this group's cluster
   *  (the focused section's per-touchpoint wall), filtered from the full mode
   *  wall. The block stays IA-agnostic — it filters by the server's group key. */
  focusGroup?: string;
}) {
  const mode = modeOfScope(scope);
  // House is the only view — the Diverging toggle + the value/count weight toggle
  // were removed (clutter + the weight toggle round-tripped the wall on every
  // flip). The wall always renders the house view of the default weighting.
  const view: PulseViewMode = "house";
  // `keepPreviousData` so a re-render / revalidation never drops the wall back to
  // the skeleton (the navigation "flash") — the bars stay put and update in place.
  // The global SWR cache is keyed by scope, so re-landing on a same-scope wall
  // (root ⇄ sub-page) reads cached data synchronously: no refetch, no skeleton.
  const { data, error, isLoading } = useApi(
    pulse.aggregate,
    {
      params: { scope },
      query: { weight, days, rollup, projectSlug },
    },
    { keepPreviousData: true },
  );

  // The SWR cache is hydrated synchronously from localStorage on the client
  // (_chrome/swr-provider.tsx), so a persisted pulse key returns `data` on the
  // FIRST client render while the server — empty cache — rendered the skeleton.
  // Rendering data on render #0 would mismatch the server HTML, so we hold the
  // skeleton until hydrated; the already-cached wall reveals on the next render
  // (no refetch), and later client navigations skip the skeleton (useHydrated).
  const hydrated = useHydrated();

  if (!hydrated || (isLoading && !data)) {
    return <PulseSkeleton size={size} className={className} />;
  }
  if (error) {
    return (
      <Card className={className}>
        <CardContent className="py-8 text-center type-body text-muted-foreground">
          Couldn&rsquo;t load the wall.
        </CardContent>
      </Card>
    );
  }

  const groups = (data?.groups ?? [])
    .filter((g) => g.touches.length > 0)
    // Section dive: narrow the full mode wall to the focused section's cluster.
    .filter((g) => !focusGroup || g.key === focusGroup);
  const allLive = groups.flatMap((g) => g.touches.filter(isLive));

  // Empty state. Two cases, NOT one dead-end:
  //   · NO bands at all (an unbuilt provider, or a focusGroup that matched
  //     nothing) → the honest "nothing here" message, every size.
  //   · Bands exist but none is LIVE yet (a portfolio scope with no subjects —
  //     e.g. a workspace with no customers — so every bar is an honest ghost).
  //     The FULL mode-dashboard wall must NOT dead-end here: it falls through and
  //     renders the bands as their per-band Setup-door on-ramps (the same
  //     uninstrumented treatment a single empty band already gets on an
  //     otherwise-live wall), turning the empty dashboard into an actionable
  //     "connect a source / set up the pipeline" wall instead of a flat sentence.
  //     Only the COMPACT embeds (the strip atop a list, the mini Object-header
  //     wall) keep the terse line — a row of Setup doors doesn't fit those slots.
  if (!groups.length || (!allLive.length && size !== "full")) {
    return (
      <Card className={className}>
        <CardContent className="py-8 text-center type-body text-muted-foreground">
          No instrument is live for this scope yet.
        </CardContent>
      </Card>
    );
  }

  // The worst live bar still gets a danger outline on the wall (§0) — the only
  // status-strip value still needed now that the strip header itself is gone.
  const worst = [...allLive].sort((a, b) => a.health - b.health)[0] ?? null;

  const track = size === "mini" ? TRACK_MINI : size === "strip" ? TRACK_STRIP : TRACK_FULL;
  const showFooter = size !== "mini";

  // Treatment C (the clickable-section pattern), now the layout for BOTH altitudes:
  // when the server has marked each section a door (`group.href`), render every
  // section as its own clickable card that dives into the section home — bars keep
  // their in-wall drill (see ClusterCard). The mode-root hero (`full`) draws the
  // cards tall; a sub-page (`strip`) draws the SAME cards, only shorter + tighter
  // (`compact`). That's the "quiet spine": entering a section just SHRINKS the wall
  // — the section labels, chevrons, health numbers, and the card-lift hover stay
  // exactly where they were (no reposition, no dim, no spotlight). Mini embeds + the
  // section rollup (no `group.href`) keep the seamless single-wall layout below.
  const cardWall = (size === "full" || size === "strip") && groups.some((g) => g.href);

  if (cardWall) {
    const compact = size === "strip";
    return (
      <WallTipLayer
        groups={groups}
        className={[
          "flex flex-col gap-3",
          // Landing on a sub-page pops the low-profile wall in, matching the hero's
          // ZoomFrame entrance. On a persistent layout (sub ⇄ sub) the wall doesn't
          // remount, so this only fires when first entering the mode.
          compact ? "origin-top animate-in fade-in zoom-in-95 duration-300 ease-out" : "",
          className ?? "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <div
          className="group/wall flex items-end gap-0.5 rounded-md"
          style={{ background: PULSE_STAGE, paddingTop: "calc(var(--spacing) * 3)", paddingBottom: "calc(var(--spacing) * 8.5)", paddingLeft: "calc(var(--spacing) * 2.5)", paddingRight: "calc(var(--spacing) * 2.5)" }}
        >
          {groups.map((g) =>
            bandState(g) === "uninstrumented" ? (
              <EmptyCell
                key={g.key}
                group={g}
                track={track}
                compact={compact}
                isCurrent={isCurrentSection(g, current)}
              />
            ) : (
              <ClusterCard
                key={g.key}
                group={g}
                worstId={worst?.id}
                track={track}
                compact={compact}
                isCurrent={isCurrentSection(g, current)}
                onTouch={onTouch ? (tid) => onTouch(g.key, tid) : undefined}
              />
            ),
          )}
        </div>
      </WallTipLayer>
    );
  }

  return (
    <WallTipLayer groups={groups}>
      {/* The strip isn't wrapped in the navigator's ZoomFrame, so it carries its
          own scale-in "pop" entrance — landing on a sub-page pops the low-profile
          wall in, matching the hero. (On a persistent layout — Agents sub ⇄ sub —
          the strip doesn't remount, so this doesn't re-fire; only the emphasis
          glides. The flash that used to ride along is gone via keepPreviousData.) */}
      <Card
        className={[
          size === "strip" ? "origin-top animate-in fade-in zoom-in-95 duration-300 ease-out" : "",
          className ?? "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <CardContent className="p-0">
          <div
            className="group/wall flex items-end gap-0.5 rounded-md"
            style={
              size === "mini"
                ? { background: PULSE_STAGE, padding: "calc(var(--spacing) * 1.5)" }
                : { background: PULSE_STAGE, paddingTop: "calc(var(--spacing) * 3)", paddingBottom: "calc(var(--spacing) * 8.5)", paddingLeft: "calc(var(--spacing) * 2.5)", paddingRight: "calc(var(--spacing) * 2.5)" }
            }
          >
            {groups.map((g, gi) => (
              <AggCluster
                key={g.key}
                group={g}
                first={gi === 0}
                worstId={worst?.id}
                track={track}
                showFooter={showFooter}
                size={size}
                mode={mode}
                scope={scope}
                editable={editable}
                view={size === "full" ? view : "house"}
                onTouch={onTouch ? (tid) => onTouch(g.key, tid) : undefined}
              />
            ))}
          </div>
        </CardContent>
      </Card>
    </WallTipLayer>
  );
}

// ── skeleton ──────────────────────────────────────────────────────────────────

function PulseSkeleton({ size, className }: { size: PulseSize; className?: string }) {
  const track = size === "mini" ? TRACK_MINI : size === "strip" ? TRACK_STRIP : TRACK_FULL;
  return (
    <Card className={className}>
      <CardContent className="p-0">
        <div className="flex items-end gap-0.5 px-4 pb-3 pt-4" style={{ height: track + 24 }}>
          {Array.from({ length: 20 }).map((_, i) => (
            <span
              key={i}
              className="flex-1 animate-pulse rounded-xs bg-muted/40"
              style={{ height: `${30 + ((i * 37) % 60)}%` }}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ── zoom transition (ported `_zoom.tsx` ZoomFrame) ────────────────────────────
//
// Deeper dives grow in (scale-[0.96]→100), climbing pulls back (scale-[1.04]→100);
// the inner is remounted per level via `key` so the rAF re-fires each transition.

function ZoomFrame({
  keyId,
  dir,
  className,
  children,
}: {
  keyId: string;
  dir: "in" | "out";
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <ZoomFrameInner key={keyId} dir={dir} className={className}>
      {children}
    </ZoomFrameInner>
  );
}

function ZoomFrameInner({
  dir,
  className,
  children,
}: {
  dir: "in" | "out";
  className?: string;
  children: React.ReactNode;
}) {
  const [shown, setShown] = useState(false);
  const raf = useRef<number | null>(null);
  useEffect(() => {
    raf.current = requestAnimationFrame(() => setShown(true));
    return () => {
      if (raf.current) cancelAnimationFrame(raf.current);
    };
  }, []);
  // The scale-in "pop" entrance (deeper dives grow in, climbing pulls back). This
  // is the liked entrance — the wall pops/grows in on mount + on each drill. The
  // earlier "violent" feel came from the skeleton flash + height shuffle on
  // navigation (fixed by keepPreviousData + the route crossfade), NOT this pop.
  const initial = dir === "in" ? "scale-[0.96] opacity-0" : "scale-[1.04] opacity-0";
  return (
    <div
      className={`origin-top transition-all duration-300 ease-out ${
        shown ? "scale-100 opacity-100" : initial
      } ${className ?? ""}`}
    >
      {children}
    </div>
  );
}
